<template>
  <article :class="cardClass" class="card card--event">
    <div class="card__inner">
      <MediaCardImage
        :bg-color="mediaBgColor"
        :card-url="pageLink"
        :card-url-title="title"
        :fit="mediaFit"
        :max-width="600"
        :media="media"
        class="media--card"
      />
      <div class="card__content">
        <div>
          <TagsList
            :audiences="audiences"
            :sorting="isDefaultTagsSorting"
            :topics="topics"
            :use-audience-as-primary="isPrimaryAudience"
          />
          <BlockLink v-if="title" :label="title" :link="pageLink">
            <h3 :class="{ card__title: !isFeatureCard && !isFeatureCard }">
              {{ title }}
            </h3>
          </BlockLink>
          <p v-if="introduction" class="card__intro hidden-xs">
            {{ trimText(introduction, 120, !isFeatureCard) }}
          </p>
        </div>
        <div class="card__meta">
          <div class="card__meta__group">
            <div
              v-if="eventStartFormatted && !isRegularEvent"
              class="date hidden-xs hidden-sm"
            >
              <span aria-label="Event Date" class="icon" role="img">
                <Icon name="fa6-regular:calendar" />
              </span>
              <span>
                {{ eventStartFormatted }}
                <span
                  v-if="eventEndFormatted && !(eventIsSameDay || eventIsAllDay)"
                >
                  – {{ eventEndFormatted }}
                </span>
              </span>
            </div>
            <div v-if="eventLocation" class="place">
              <span aria-label="Event Location" class="icon" role="img">
                <Icon name="fa6-solid:location-dot" />
              </span>
              <span>
                {{ eventLocation }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <EventsDateOverlay v-if="!isRegularEvent" :blok="blok" />
    </div>
  </article>
</template>

<script setup lang="ts">
import { trimText } from '#imports'
import useEventContent from '~/composables/useEvent'
import type { EventsStoryblok } from '~/types/storyblok'
const props = withDefaults(
  defineProps<{
    blok: EventsStoryblok
    customStory?: StoryContentBlok
    cardType?: string
  }>(),
  {
    cardType: null,
    customStory: () => undefined,
  },
)
const storyContent = computed(() => props.blok?.content)
const {
  customLink,
  title,
  introduction,
  isPrimaryAudience,
  audiences,
  isDefaultTagsSorting,
  topics,
  mediaBgColor,
  mediaFit,
  media,
} = useStoryContent({ ...toRefs(props), storyContent })
const {
  eventEndFormatted,
  eventIsAllDay,
  eventLocation,
  isRegularEvent,
  eventIsSameDay,
  eventStartFormatted,
} = useEventContent(toRefs(props))
const pageLink = computed(() => customLink.value || '/' + props.blok?.full_slug)
const isFeatureCard = computed(() => props.cardType === 'featured')
const isVerticalCard = computed(() => props.cardType === 'vertical')
const cardClass = computed(() =>
  isFeatureCard.value
    ? 'card--featured card--vertical-below-tablet'
    : isVerticalCard.value
      ? 'card--vertical card--event-vertical'
      : '',
)
</script>

<style lang="less" scoped>
.card.card--event {
  .card__meta {
    .font-medium();
    .text-body();
  }

  &.card--vertical {
    .card__content {
      min-height: @150px;
    }
  }

  &.card--featured {
    .media {
      flex-basis: calc(50% - (@72px / 2));
    }

    .card__content {
      height: auto;
      padding-bottom: @gutter;

      @media @tablet {
        padding: @gutter-xxl @gutter-xxl @60px @72px;
      }

      .tag-list {
        @media @tablet {
          margin-bottom: @gutter;
        }
      }

      h3 {
        @media @tablet {
          .font-size(27, 33);
          .font-black();
          .space(0);
        }
      }

      p.card__intro {
        @media @tablet {
          .h4();

          margin: @gutter 0 @gutter-lg;
        }
      }
    }

    .event-date-overlay {
      top: -@6px;

      .start,
      .end {
        padding: @4px @8px @6px;
        border-top: solid @6px @color-tertiary;

        @media @desktop {
          padding: @gutter-xxs @gutter-xxs @8px;
        }

        .day {
          .font-size(23, 25);

          @media @desktop {
            .font-size(33, 36);
          }
        }

        .month {
          .font-size(12, 14);

          @media @desktop {
            .font-size(15, 16);
          }
        }
      }

      .end {
        border-top-color: @color-primary;
      }
    }
  }
}
</style>
